import React, { useEffect, useRef } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { BOTTOM_INDENTS, COLORS, ROUTE_PATHS, TOP_INDENTS } from '@constants';
import { useStore } from '@hooks';
import { Button, Divider, Icon, Typography } from '@ui';
import { formatString } from '@utils';
import { Flex, notification } from 'antd';
import { observer } from 'mobx-react';
import { PreWizardProvider, usePreWizardContext } from '../PreWizardContext';
import { HeaderRow, StyledLayout } from './styled';

const CampaignPreWizardLayoutContent = observer(() => {
    const store = useStore();
    const { isManualStoreWithNoCampaigns } = store.accountsStore;
    const { resetCampaign, resetPreviewProducts } = store.campaignWizardStore;
    const location = useLocation();
    const { isButtonEnabled, onButtonClick, setSelectedOption, setIsButtonEnabled, setOnButtonClick, isSubmitting } =
        usePreWizardContext();
    const previousPathRef = useRef(location.pathname);

    useEffect(() => {
        return () => {
            resetCampaign();
            resetPreviewProducts();
        };
    }, [resetCampaign, resetPreviewProducts]);

    useEffect(() => {
        const currentPath = location.pathname;
        const previousPath = previousPathRef.current;

        if (previousPath !== currentPath) {
            setSelectedOption(null);
            setIsButtonEnabled(false);
            setOnButtonClick(() => null);
            previousPathRef.current = currentPath;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    const isPreWizardLanding = location.pathname === ROUTE_PATHS.CAMPAIGNS_PRE_WIZARD;
    const isManualFeed = location.pathname === ROUTE_PATHS.CAMPAIGNS_CREATE_MANUAL_FEED;
    const isManualCsv = location.pathname === ROUTE_PATHS.CAMPAIGNS_CREATE_MANUAL_CSV;
    const isManualApi = location.pathname === ROUTE_PATHS.CAMPAIGNS_CREATE_MANUAL_API;
    const isMetaCatalogs = location.pathname === ROUTE_PATHS.CAMPAIGNS_CREATE_MANUAL_FEED_META_CATALOGS;

    const showButton = !isManualApi && !isMetaCatalogs;

    let buttonText = 'Next';
    if (isPreWizardLanding) {
        buttonText = 'Connect Products';
    } else if (isManualFeed) {
        buttonText = 'Sync Catalog';
    } else if (isManualCsv) {
        buttonText = 'Create Campaign';
    }

    const isDisabled = !isButtonEnabled;

    const handleButtonClick = () => {
        if (onButtonClick) {
            try {
                const handler = onButtonClick();
                if (typeof handler === 'function') {
                    handler();
                }
            } catch (error) {
                notification.error({
                    message: 'Something went wrong'
                });
            }
        }
    };

    if (!isManualStoreWithNoCampaigns) {
        return <Navigate to={ROUTE_PATHS.CAMPAIGNS} replace />;
    }

    return (
        <StyledLayout>
            <Flex vertical style={{ flexGrow: 1 }}>
                <HeaderRow>
                    <Typography.Text type='em0'>Create Campaign</Typography.Text>

                    {isMetaCatalogs && (
                        <Button
                            data-e2e='onboarding-wizard_sync-catalog_btn'
                            disabled={isDisabled || isSubmitting}
                            loading={isSubmitting}
                            type='primary'
                            size='large'
                            onClick={handleButtonClick}
                            iconRight={
                                <Icon
                                    size='24px'
                                    style={{
                                        color: COLORS.$white
                                    }}
                                    component={() => <Icon.ArrowForwardIcon />}
                                />
                            }
                        >
                            Sync Catalog
                        </Button>
                    )}

                    {showButton && (
                        <Button
                            data-e2e={`onboarding-wizard_${formatString(buttonText)}_btn`}
                            disabled={isDisabled}
                            type='secondary'
                            size='large'
                            onClick={handleButtonClick}
                            style={{
                                color: COLORS.$white,
                                backgroundColor: isDisabled ? COLORS.$gray20Black : COLORS.$pink
                            }}
                            iconRight={
                                <Icon
                                    size='24px'
                                    style={{
                                        color: COLORS.$white
                                    }}
                                    component={() => <Icon.ArrowForwardIcon />}
                                />
                            }
                        >
                            {buttonText}
                        </Button>
                    )}
                </HeaderRow>
                <Divider
                    style={{ ...BOTTOM_INDENTS.XL_PLUS, ...TOP_INDENTS.XL_PLUS, borderColor: COLORS.$gray20Black }}
                />
                <Outlet />
            </Flex>
        </StyledLayout>
    );
});

const CampaignPreWizardLayout = () => {
    return (
        <PreWizardProvider>
            <CampaignPreWizardLayoutContent />
        </PreWizardProvider>
    );
};

export default CampaignPreWizardLayout;
